import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import FavoriteIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteFilledIcon from '@mui/icons-material/Favorite';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOfferOutlined';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined';
import ClaimPopup from '../ClaimPopup';
import InfoPopup from '../InfoPopup';
import { getQuantityUrgencyColor } from 'utils/UI';
import { offerExpiration } from 'utils/offerExpiration';
import useFavoriteOffer from '../useFavoriteOffer';
import { offerAvailabilitySx } from '../utils';

const formatPhone = (input = "") => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  let formatted = "";
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};

const isPhoneFormatted = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

const formatPhoneIfNeeded = (phone) => {
  if (!phone) return phone;
  if (isPhoneFormatted(phone)) return phone;
  return formatPhone(phone);
};

const MobileOfferCard = ({
  active,
  id,
  description,
  claimsRemaining,
  termsAndConditions,
  distance,
  endDate,
  imageUrl,
  businessId,
  businessImageUrl,
  businessName,
  headline1,
  headline2,
  isPreview,
  phone,
  showPhone,
  address,
  showAddress,
  revalidateList,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const { isFavorite, isUpdating, toggleFavorite } = useFavoriteOffer(id, {
    disabled: isPreview,
    revalidateList,
  });
  const [showClaimPopup, setShowClaimPopup] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);

  const isNotSearch = location?.pathname !== "/search";

  const handleBusinessClick = useCallback(() => {
    if (isPreview) return;
    if (isNotSearch) {
      navigate(`/search?businessId=${businessId}`);
    } else {
      setSearchParams({ businessId });
    }
  }, [businessId, navigate, isNotSearch, isPreview, setSearchParams]);

  const endDateText = offerExpiration(endDate).label;

  // Quantity label
  const qty = isPreview ? 100 : claimsRemaining;
  const qtyLabel = qty ? `${qty} left` : 'Depleted';

  // Distance display
  const distanceText = distance && distance !== 'Global' ? distance : null;

  const handleOpenMaps = () => {
    if (!address) return;
    
    // Encode the address for URL
    const encodedAddress = encodeURIComponent(address);
    
    // Detect iOS devices
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      // Use Apple Maps on iOS
      window.open(`https://maps.apple.com/?q=${encodedAddress}`, '_blank');
    } else {
      // Use Google Maps on Android and Web
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    }
  };

  return (
    <>
      <Card
        sx={{
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          boxShadow: 'none',
          ...offerAvailabilitySx(active, isPreview),
          mb: 2,
          marginLeft: 0,
          marginRight: 0,
          boxSizing: 'border-box',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header Section: Business Name, Distance, and Thumbnail */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {businessName || headline1}
              </Typography>
              {headline2 && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 400,
                    mb: 0.5,
                  }}
                >
                  {headline2}
                </Typography>
              )}
              {distanceText && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    fontSize: '0.875rem',
                  }}
                >
                  {distanceText}
                </Typography>
              )}
            </Box>
            {businessImageUrl && (
              <Avatar
                src={businessImageUrl}
                alt={businessName || businessId}
                onClick={handleBusinessClick}
                sx={{
                  width: 56,
                  height: 56,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          {/* Description */}
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.875rem',
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {description.length > 150 ? description.substr(0, 150) + '...' : description}
            </Typography>
          )}

          {/* Pills: Quantity and End Date */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={qtyLabel}
              sx={(theme) => ({
                backgroundColor: getQuantityUrgencyColor(qty, theme),
                color: theme.palette.getContrastText(getQuantityUrgencyColor(qty, theme)),
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
              })}
            />
            <Chip
              label={endDateText}
              icon={<QueryBuilderIcon sx={{ fontSize: '0.875rem !important' }} />}
              variant="outlined"
              sx={{
                borderColor: '#e0e0e0',
                color: 'text.secondary',
                fontWeight: 400,
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-icon': {
                  color: 'text.secondary',
                },
              }}
            />
          </Box>

          {/* Action Icons and Claim Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {showPhone && (
                <Tooltip title="Call business" enterDelay={500}>
                  <IconButton
                    size="small"
                    disabled={isPreview || !active}
                    onClick={() => setShowPhonePopup(true)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <PhoneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {showAddress && address && (
                <Tooltip title="Directions" enterDelay={500}>
                  <IconButton
                    size="small"
                    disabled={isPreview || !active}
                    onClick={() => setShowLocationPopup(true)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <LocationOnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Visit website" enterDelay={500}>
                <IconButton
                  size="small"
                  disabled={isPreview || !active}
                  href={`/api/common/offer_homepage/${id}`}
                  target="_blank"
                  rel="nofollow"
                  sx={{ color: 'text.secondary' }}
                >
                  <HomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                enterDelay={500}
              >
                <IconButton
                  size="small"
                  onClick={toggleFavorite}
                  disabled={isPreview || isUpdating}
                  sx={{ color: isFavorite ? 'error.main' : 'text.secondary' }}
                >
                  {isFavorite ? (
                    <FavoriteFilledIcon fontSize="small" />
                  ) : (
                    <FavoriteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Terms & conditions" enterDelay={500}>
                <IconButton
                  size="small"
                  disabled={isPreview || !active}
                  onClick={() => setShowInfoPopup(true)}
                  sx={{ color: 'text.secondary' }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<LocalOfferIcon />}
              disabled={isPreview || !active}
              onClick={() => setShowClaimPopup(true)}
              sx={{
                minWidth: 80,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              Claim
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Popups */}
      {showClaimPopup && (
        <ClaimPopup
          closePopup={() => setShowClaimPopup(false)}
          headline1={headline1}
          headline2={headline2}
          description={description}
          distance={distance}
          claims={claimsRemaining}
          endDate={endDate}
          id={id}
          active={active}
          address={address}
          showAddress={showAddress}
          phone={phone}
          showPhone={showPhone}
          revalidateList={revalidateList}
          businessName={businessName}
          businessImageUrl={businessImageUrl}
        />
      )}

      {showInfoPopup && (
        <InfoPopup
          openClaim={() => setShowClaimPopup(true)}
          businessImageUrl={businessImageUrl}
          claimsRemaining={claimsRemaining}
          closePopup={() => setShowInfoPopup(false)}
          endDate={endDate}
          termsAndConditions={termsAndConditions}
          headline1={headline1}
          headline2={headline2}
          id={id}
          imageUrl={imageUrl}
          location={distance}
        />
      )}

      <Dialog open={showPhonePopup} onClose={() => setShowPhonePopup(false)}>
        <DialogTitle>{businessName ? `${businessName} phone` : 'Business phone'}</DialogTitle>
        <DialogContent>
          {phone ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {formatPhoneIfNeeded(phone)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhoneIcon />}
                  href={`tel:${phone.replace(/\D/g, '')}`}
                >
                  Call
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Phone number not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPhonePopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showLocationPopup} onClose={() => setShowLocationPopup(false)}>
        <DialogTitle>{businessName ? `${businessName} ` : 'Business address'}</DialogTitle>
        <DialogContent>
          {address ? (
            <>
              <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
                {address}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LocationOnIcon />}
                  onClick={handleOpenMaps}
                >
                  Directions
                </Button>
              </Box>
            </>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Address not available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLocationPopup(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MobileOfferCard;
